import { useCallback, useEffect, useRef } from 'react';

import { type OptimisticMutationUpdateState } from './optimisticMutationUpdate.ts';

// Tracks optimistic update layers created by a mounted mutation hook. Unmounting
// does not mean the native mutation was cancelled, so cleanup only detaches
// abandoned layers from later rollback/reapply cycles; it does not restore data.
export function usePendingOptimisticInteropUpdates<TData>(): {
	trackOptimisticUpdate: (optimisticUpdateState: OptimisticMutationUpdateState<TData>) => void;
	untrackOptimisticUpdate: (optimisticUpdateState: OptimisticMutationUpdateState<TData>) => void;
} {
	const pendingOptimisticUpdatesRef = useRef<Set<OptimisticMutationUpdateState<TData>>>(new Set());

	useEffect(() => {
		const pendingOptimisticUpdates = pendingOptimisticUpdatesRef.current;

		return () => {
			for (const optimisticUpdateState of pendingOptimisticUpdates) {
				optimisticUpdateState.cleanupOptimisticUpdate();
			}
			pendingOptimisticUpdates.clear();
		};
	}, []);

	const trackOptimisticUpdate = useCallback(
		(optimisticUpdateState: OptimisticMutationUpdateState<TData>): void => {
			pendingOptimisticUpdatesRef.current.add(optimisticUpdateState);
		},
		[],
	);

	const untrackOptimisticUpdate = useCallback(
		(optimisticUpdateState: OptimisticMutationUpdateState<TData>): void => {
			pendingOptimisticUpdatesRef.current.delete(optimisticUpdateState);
		},
		[],
	);

	return { trackOptimisticUpdate, untrackOptimisticUpdate };
}
